// Filter name mappings for UI to API conversion
export const FILTER_NAME_MAPPING = {
  ratePlans: "RATEPLAN",
  roomTypes: "ROOMNAME",
  promotions: "PROMOTION",
  mealPlans: "MEALPLAN",
  refundableOptions: "REFUNDABLE",
};

// Reverse mapping for API to UI conversion
export const API_FILTER_NAME_MAPPING = {
  RATEPLAN: "ratePlans",
  ROOMNAME: "roomNames",
  PROMOTION: "promotions",
  MEALPLAN: "mealPlans",
  REFUNDABLE: "refundableOptions",
};

/**
 * Process UI filters for API consumption
 * @param {Object} uiFilters - Raw UI filter values
 * @returns {Object} Processed filters ready for API
 */
export const processFiltersForAPI = (uiFilters) => ({
  ratePlans:
    uiFilters.ratePlans
      ?.filter((val) => val !== "all")
      ?.map((val) => (val === "cheapest" ? "CHEAPEST_RATEPLAN" : val)) || [],
  roomNames:
    uiFilters.roomTypes
      ?.filter((val) => val !== "all")
      ?.map((val) => (val === "cheapest" ? "CHEAPEST_ROOMTYPE" : val)) || [],
  promotions: uiFilters.promotions?.filter((val) => val !== "all") || [],
  mealPlans: uiFilters.mealPlans?.filter((val) => val !== "all") || [],
  refundableOptions:
    uiFilters.refundableOptions?.filter((val) => val !== "all") || [],
});

/**
 * Convert UI filter selection order to API format
 * @param {Array<string>} uiFilterOrder - UI filter names in selection order
 * @returns {Array<string>} API filter names in selection order
 */
export const convertFilterOrderToAPI = (uiFilterOrder) =>
  uiFilterOrder
    .map((filterType) => FILTER_NAME_MAPPING[filterType])
    .filter(Boolean);

/**
 * Update filter selection order based on new filter value
 * @param {Array<string>} currentOrder - Current filter selection order
 * @param {string} filterType - Filter type being updated
 * @param {Array<string>} newValue - New filter value
 * @returns {Array<string>} Updated filter selection order
 */
export const updateFilterSelectionOrder = (
  currentOrder,
  filterType,
  newValue
) => {
  // Always add the filterType to the stack if newValue has any content
  // This includes when "all" is explicitly selected
  if (newValue && newValue.length > 0) {
    return [...new Set([...currentOrder, filterType])];
  }
  // Only remove from stack if newValue is undefined or empty array
  return currentOrder.filter((type) => type !== filterType);
};

/**
 * Build filter stack for API payload
 * @param {Array<string>} filterOrder - Filter order (user selection or predefined)
 * @param {Object} filters - Filter values
 * @returns {Array<string>} Final filter stack
 */
export const buildFilterStack = (filterOrder, filters) => {
  const defaultOrder = [
    "RATEPLAN",
    "ROOMNAME",
    "PROMOTION",
    "MEALPLAN",
    "REFUNDABLE",
  ];
  const orderToUse = filterOrder?.length > 0 ? filterOrder : defaultOrder;

  return orderToUse.filter((filterType) => {
    const filterKey = API_FILTER_NAME_MAPPING[filterType];
    return filterKey && filters[filterKey]?.length > 0;
  });
};

/**
 * Check if any filters are applied
 * @param {Object} filters - Filter values
 * @returns {boolean} True if any filters have values
 */
export const hasActiveFilters = (filters) =>
  Object.values(filters).some((filterArray) => filterArray?.length > 0);

/**
 * Get filter count for display
 * @param {Object} filters - Filter values
 * @returns {number} Number of active filters
 */
export const getActiveFilterCount = (filters) =>
  Object.values(filters).filter((filterArray) => filterArray?.length > 0)
    .length;
